import {
  Building2,
  ClipboardCheck,
  Contact,
  Copy,
  DoorOpen,
  FileSignature,
  Cable,
  FileText,
  Gauge,
  History,
  Import,
  Inbox,
  KeyRound,
  LayoutDashboard,
  Link,
  Mail,
  Megaphone,
  MessageSquare,
  Send,
  Settings,
  Signpost,
  ShieldCheck,
  TrendingUp,
  UserCog,
  Users,
  Wallet,
  Receipt,
  Handshake,
} from 'lucide-react';

/**
 * Admin navigation (docs/frontend/admin.md §1). `access` follows the permission
 * names of docs/architecture/permissions.md §3 – UX only, the API checks again.
 *
 * `badge`: key of a live counter rendered next to the label (see layout/app-shell.jsx `NAV_BADGES`).
 *
 * @typedef {{ id: string, label: string, to: string, icon: any, access?: import('@blimo/shared').AccessRule, features?: string, badge?: string }} NavItem
 * @typedef {{ id: string, label?: string, items: NavItem[] }} NavSection
 */

/** @type {NavSection[]} */
export const adminNav = [
  {
    id: 'start',
    items: [{ id: 'dashboard', label: 'Dashboard', to: '/', icon: LayoutDashboard, access: { permissions: ['admin.access'] } }],
  },
  {
    id: 'verwaltung',
    label: 'Verwaltung',
    items: [
      {
        id: 'properties',
        label: 'Liegenschaften',
        to: '/liegenschaften',
        icon: Building2,
        access: { permissions: ['properties.view'] },
        features: 'F-110–F-115, F-401',
      },
      { id: 'units', label: 'Einheiten', to: '/einheiten', icon: DoorOpen, access: { permissions: ['units.view'] }, features: 'F-120–F-122' },
      {
        id: 'parties',
        label: 'Personen & Organisationen',
        to: '/personen',
        icon: Contact,
        access: { permissions: ['parties.view'] },
        features: 'F-101–F-104, F-014',
      },
      {
        id: 'tenancies',
        label: 'Mietverhältnisse',
        to: '/mietverhaeltnisse',
        icon: KeyRound,
        access: { permissions: ['tenancies.view'] },
        features: 'F-702–F-704',
      },
      { id: 'lettings', label: 'Vermietung', to: '/vermietung', icon: Signpost, access: { permissions: ['tenancies.view'] }, features: 'F-608, F-1002' },
    ],
  },
  {
    id: 'finanzen',
    label: 'Finanzen',
    items: [
      { id: 'open-items', label: 'Offene Posten', to: '/finanzen/offene-posten', icon: Wallet, access: { permissions: ['finance.view'] }, features: 'F-606' },
      { id: 'costs', label: 'Kosten', to: '/finanzen/kosten', icon: Receipt, access: { permissions: ['finance.view'] }, features: 'F-604' },
      {
        id: 'rent-development',
        label: 'Mietentwicklung & Rendite',
        to: '/finanzen/mietentwicklung',
        icon: TrendingUp,
        access: { permissions: ['finance.view'] },
        features: 'F-602, F-603',
      },
      {
        id: 'mandates',
        label: 'Mandanten & Freigaben',
        to: '/finanzen/mandanten',
        icon: Handshake,
        access: { permissions: ['mandates.manage', 'finance.view'] },
        features: 'F-013',
      },
    ],
  },
  {
    id: 'zaehler',
    label: 'Zähler',
    items: [
      {
        id: 'meter-readings',
        label: 'Zählerstände (Jahr)',
        to: '/zaehler/staende',
        icon: Gauge,
        access: { permissions: ['meters.view'] },
        features: 'F-903–F-906, F-908 (Exporte)',
      },
      { id: 'meters', label: 'Zähler & Versorger', to: '/zaehler', icon: Cable, access: { permissions: ['meters.view'] }, features: 'F-901, F-902' },
      { id: 'meter-readers', label: 'Ableser', to: '/zaehler/ableser', icon: Users, access: { permissions: ['meters.view'] }, features: 'F-907, F-909' },
    ],
  },
  {
    id: 'eigentuemerversammlung',
    label: 'Eigentümerversammlung',
    items: [
      {
        id: 'proxy-authorizations',
        label: 'Stimmrechtsvollmachten',
        to: '/eigentuemerversammlung/vollmachten',
        icon: FileSignature,
        access: { permissions: ['proxy-authorizations.view'] },
        features: 'F-1001',
      },
    ],
  },
  {
    id: 'belegpruefung',
    items: [
      {
        id: 'document-checks',
        label: 'Belegprüfung',
        to: '/belegpruefung',
        icon: ClipboardCheck,
        access: { permissions: ['document-checks.view'] },
        features: 'F-801–F-809',
      },
    ],
  },
  {
    id: 'kommunikation',
    label: 'Kommunikation',
    items: [
      {
        id: 'announcements',
        label: 'Mitteilungen',
        to: '/kommunikation/mitteilungen',
        icon: Megaphone,
        access: { permissions: ['announcements.view'] },
        features: 'F-501, F-502',
      },
      {
        id: 'inbox',
        label: 'Posteingang',
        to: '/kommunikation/posteingang',
        icon: Inbox,
        access: { permissions: ['messages.view'] },
        features: 'F-504 (Eigentümer-Nachrichten)',
        badge: 'messages-open',
      },
      {
        id: 'comments',
        label: 'Kommentare',
        to: '/kommunikation/kommentare',
        icon: MessageSquare,
        access: { permissions: ['comments.moderate'] },
        features: 'F-505',
      },
      {
        id: 'outbound',
        label: 'Versandhistorie',
        to: '/kommunikation/versandhistorie',
        icon: Send,
        access: { permissions: ['outbound-messages.view'] },
        features: 'F-507',
      },
      { id: 'templates', label: 'Vorlagen', to: '/kommunikation/vorlagen', icon: Mail, access: { permissions: ['mail-templates.manage'] }, features: 'F-506' },
    ],
  },
  {
    id: 'dokumente',
    label: 'Dokumente',
    items: [
      {
        id: 'forms',
        label: 'Formulare & Downloads',
        to: '/dokumente/formulare',
        icon: FileText,
        access: { permissions: ['documents.view'] },
        features: 'F-407',
      },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    items: [
      {
        id: 'users',
        label: 'Benutzer & Zugänge',
        to: '/administration/benutzer',
        icon: UserCog,
        access: { permissions: ['users.view'] },
        features: 'F-003, F-011, F-012, F-005',
      },
      { id: 'links', label: 'Nützliche Links', to: '/administration/links', icon: Link, access: { permissions: ['links.manage'] }, features: 'F-1003' },
      { id: 'roles', label: 'Rollen & Rechte', to: '/administration/rollen', icon: ShieldCheck, access: { permissions: ['roles.manage'] }, features: 'F-003' },
      {
        id: 'imports',
        label: 'Importe (Cobra, Buchhaltung)',
        to: '/administration/importe',
        icon: Import,
        access: { permissions: ['import-runs.view'] },
        features: 'F-301–F-309',
      },
      {
        id: 'duplicates',
        label: 'Dubletten-Prüfung',
        to: '/administration/dubletten',
        icon: Copy,
        access: { permissions: ['parties.update'] },
        features: 'Migration §5',
      },
      {
        id: 'settings',
        label: 'Einstellungen / Branding',
        to: '/administration/einstellungen',
        icon: Settings,
        access: { permissions: ['settings.manage'] },
        features: 'F-015, F-014',
      },
      {
        id: 'audit-log',
        label: 'Änderungsprotokoll',
        to: '/administration/aenderungsprotokoll',
        icon: History,
        access: { permissions: ['audit-log.view'] },
        features: 'Audit',
      },
    ],
  },
];

/** Flat list of all nav items (used to generate placeholder routes). */
export const adminNavItems = adminNav.flatMap((section) => section.items);
